import { Injectable, inject } from '@angular/core';
import { DialogService } from '@next/core-lib/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { catchError, map, mergeMap, of, switchMap } from 'rxjs';
import { OfferService } from 'src/app/shared/services/offers/offers.service';
import { RequestService } from 'src/app/shared/services/request.service';

// eslint-disable-next-line max-len
import { ActiveRequestsSlideInOverviewComponent } from '../../components/active-requests-slide-in/active-requests-slide-in-overview/active-requests-slide-in-overview.component';
import * as MyAdvertisementsActions from '../actions/my-advertisements.actions';

@Injectable()
export class MyAdvertisementsEffects {
  private readonly dialogService = inject(DialogService);
  private readonly actions$ = inject(Actions);
  private readonly requestsService = inject(RequestService);
  private readonly offersService = inject(OfferService);

  getActiveRequestsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MyAdvertisementsActions.loadActiveRequests),
      switchMap((x) =>
        this.requestsService.getMyActiveRequests(x.pageAndSize).pipe(
          map((requestsList) =>
            MyAdvertisementsActions.loadActiveRequestsSuccess({
              activeRequestsList: requestsList.results,
              totalNumberOfActiveRequests: requestsList.metadata.totalResultCount,
            }),
          ),
          catchError((error) =>
            of(MyAdvertisementsActions.loadActiveRequestsFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  getClosedRequestsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MyAdvertisementsActions.loadClosedRequests),
      switchMap((x) =>
        this.requestsService.getMyClosedRequests(x.paginationOptions).pipe(
          map((requestsList) =>
            MyAdvertisementsActions.loadClosedRequestsSuccess({
              closedRequestsList: requestsList.results,
            }),
          ),
          catchError((error) =>
            of(MyAdvertisementsActions.loadClosedRequestsFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  getActiveOffersList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MyAdvertisementsActions.loadActiveOffers),
      switchMap((x) =>
        this.offersService.getMyActiveOffers(x.paginationOptions).pipe(
          map((offersList) =>
            MyAdvertisementsActions.loadActiveOffersSuccess({
              activeOffersList: offersList.results,
              totalNumberOfActiveOffers: offersList.metadata.totalResultCount,
            }),
          ),
          catchError((error) =>
            of(MyAdvertisementsActions.loadActiveOffersFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  getClosedDealsOffersList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MyAdvertisementsActions.loadClosedOffers),
      switchMap((x) =>
        this.offersService.getMyClosedOffers(x.paginationOptions).pipe(
          map((offersList) =>
            MyAdvertisementsActions.loadClosedOffersSuccess({
              closedDealsOffersList: offersList.results,
            }),
          ),
          catchError((error) =>
            of(MyAdvertisementsActions.loadClosedOffersFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  openRequestOverviewDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MyAdvertisementsActions.openActiveRequestDialog),
      switchMap((actionData) =>
        this.dialogService.fullscreenDialog(
          ActiveRequestsSlideInOverviewComponent,
          {
            selectedRequest: actionData.selectedRequest,
            cancelButton: true,
            altButton: false,
            confirmButton: false,
          },
          {
            autoFocus: false,
          },
        ),
      ),
      switchMap(() => of(MyAdvertisementsActions.openActiveRequestDialogSuccess())),
    ),
  );

  requestAcceptMatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MyAdvertisementsActions.acceptMatch),
      mergeMap((action) =>
        this.requestsService
          .acceptMatch(action.requestId)
          .pipe(map((response) => MyAdvertisementsActions.acceptMatchSuccess({ response }))),
      ),
    ),
  );

  requestRejectMatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MyAdvertisementsActions.rejectMatch),
      mergeMap((action) =>
        this.requestsService
          .rejectMatch(action.requestId)
          .pipe(map((response) => MyAdvertisementsActions.rejectMatchSuccess({ response }))),
      ),
    ),
  );

  deleteOffer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MyAdvertisementsActions.deleteOffer),
      switchMap((x) =>
        this.offersService.deleteOffer(x.id).pipe(
          map((response) =>
            response.status === 200
              ? MyAdvertisementsActions.deleteOfferSuccess({ id: x.id })
              : MyAdvertisementsActions.deleteOfferFailure(),
          ),
          catchError(() => of(MyAdvertisementsActions.deleteOfferFailure())),
        ),
      ),
    ),
  );

  deleteRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MyAdvertisementsActions.deleteRequest),
      switchMap((x) =>
        this.requestsService.deleteRequest(x.id).pipe(
          map((response) =>
            response.status === 200
              ? MyAdvertisementsActions.deleteRequestSuccess({ id: x.id })
              : MyAdvertisementsActions.deleteRequestFailure(),
          ),
          catchError(() => of(MyAdvertisementsActions.deleteRequestFailure())),
        ),
      ),
    ),
  );
}
